import { createAttachmentCardElement } from './attachment';
import { attachIcon, replyIcon, sendIcon } from './icons';
import { WIDGET_FEATURE_FLAGS } from './featureFlags';
import { t, tf } from './i18n';

export interface PendingFile {
  localId: string;
  file: File;
}

export interface UploadedAttachment {
  id: string;
  originalFilename: string;
  byteSize: number;
}

export interface ReplyPreview {
  label: string;
  snippet: string;
}

export interface ComposerHandlers {
  onSend: (text: string, attachmentIds: string[]) => void;
  onFilesSelected: (files: PendingFile[]) => void;
  onRetryAttachment: (localId: string, file: File) => void;
  onCancelReply: () => void;
  // Fired at most once per TYPING_THROTTLE_MS while the customer is
  // actively typing — never per-keystroke, since this ultimately triggers
  // a real Discord API call (see widget.ts/discord/service.ts).
  onTyping: () => void;
}

export interface Composer {
  element: HTMLElement;
  setReady(isReady: boolean): void;
  setSending(isSending: boolean): void;
  setSendError(message: string | null): void;
  setAttachmentUploading(localId: string): void;
  setAttachmentProgress(localId: string, fraction: number): void;
  setAttachmentUploaded(localId: string, attachment: UploadedAttachment): void;
  setAttachmentError(localId: string, message: string): void;
  // Shows/hides the "replying to …" preview above the input. Null clears it.
  setReplyPreview(preview: ReplyPreview | null): void;
  // Stages files as pending attachments and emits onFilesSelected — the
  // shared path for both the file-picker and drag-and-drop (see widget.ts).
  addFiles(files: File[]): void;
  clear(): void;
  clearAttachments(): void;
  focus(): void;
  destroy(): void;
}

const ROOT_CLASS = 'flix-chat-widget__composer';
const ATTACHMENT_CLASS = 'flix-chat-widget__attachment';
const TYPING_THROTTLE_MS = 4000;

interface PendingAttachment {
  file: File;
  status: 'uploading' | 'uploaded' | 'error';
  attachmentId: string | null;
  cardElement: HTMLElement;
  statusElement: HTMLElement;
  progressTrack: HTMLElement;
  progressFill: HTMLElement;
  retryButton: HTMLButtonElement;
}

/**
 * Owns the textarea, the send button, the file picker, and the pending
 * attachment cards — their disabled/loading/error state, Enter/Shift+Enter
 * keyboard handling, and auto-growing the textarea's height with its
 * content up to the CSS max-height. It never touches the network itself:
 * file selection and send both only ever emit events, and every status
 * update (uploading, uploaded, error) is pushed in from the widget, which
 * owns the Client SDK.
 *
 * Also holds two permanently-hidden extension points gated by
 * WIDGET_FEATURE_FLAGS (both false today): an emoji-picker trigger button
 * and a reply-preview slot. Neither has any backing logic — see
 * featureFlags.ts — they exist only so a future feature can occupy an
 * already-styled place in this layout without restructuring it.
 */
export function createComposer(handlers: ComposerHandlers): Composer {
  const element = document.createElement('div');
  element.className = ROOT_CLASS;

  // The "replying to …" bar above the input (Discord/WhatsApp pattern),
  // shown only while a reply is in progress (see setReplyPreview).
  const replyPreviewSlot = document.createElement('div');
  replyPreviewSlot.className = `${ROOT_CLASS}-reply-preview`;
  replyPreviewSlot.hidden = true;

  const replyPreviewIcon = document.createElement('span');
  replyPreviewIcon.className = `${ROOT_CLASS}-reply-preview-icon`;
  replyPreviewIcon.append(replyIcon());

  const replyPreviewText = document.createElement('div');
  replyPreviewText.className = `${ROOT_CLASS}-reply-preview-text`;
  const replyPreviewLabel = document.createElement('div');
  replyPreviewLabel.className = `${ROOT_CLASS}-reply-preview-label`;
  const replyPreviewSnippet = document.createElement('div');
  replyPreviewSnippet.className = `${ROOT_CLASS}-reply-preview-snippet`;
  replyPreviewText.append(replyPreviewLabel, replyPreviewSnippet);

  const replyPreviewCancel = document.createElement('button');
  replyPreviewCancel.type = 'button';
  replyPreviewCancel.className = `${ROOT_CLASS}-reply-preview-cancel`;
  replyPreviewCancel.setAttribute('aria-label', t('cancelReply'));
  replyPreviewCancel.textContent = '×';
  replyPreviewCancel.addEventListener('click', () => handlers.onCancelReply());

  replyPreviewSlot.append(replyPreviewIcon, replyPreviewText, replyPreviewCancel);

  const attachmentsContainer = document.createElement('div');
  attachmentsContainer.className = `${ROOT_CLASS}-attachments`;
  attachmentsContainer.hidden = true;

  const errorText = document.createElement('p');
  errorText.className = `${ROOT_CLASS}-error`;
  errorText.hidden = true;

  const row = document.createElement('div');
  row.className = `${ROOT_CLASS}-row`;

  const textarea = document.createElement('textarea');
  textarea.className = `${ROOT_CLASS}-input`;
  textarea.rows = 1;
  textarea.placeholder = t('typeMessage');
  textarea.setAttribute('aria-label', t('messageAria'));

  // Extension point only — see the doc comment above. Never shown while
  // WIDGET_FEATURE_FLAGS.emojiPicker is false, and has no click handler.
  const emojiButton = document.createElement('button');
  emojiButton.type = 'button';
  emojiButton.className = `${ROOT_CLASS}-emoji`;
  emojiButton.setAttribute('aria-label', t('chooseEmoji'));
  emojiButton.hidden = !WIDGET_FEATURE_FLAGS.emojiPicker;
  emojiButton.textContent = '🙂';

  const attachButton = document.createElement('button');
  attachButton.type = 'button';
  attachButton.className = `${ROOT_CLASS}-attach`;
  attachButton.setAttribute('aria-label', t('attachFile'));
  attachButton.append(attachIcon());

  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.multiple = true;
  fileInput.hidden = true;
  // Keeps the OS file picker from ever suggesting an unsupported type —
  // must stay in sync with the server's own ALLOWED_UPLOAD_MIME_TYPES
  // (features/files/validation.ts), which remains the actual source of
  // truth and is enforced regardless of this hint (a mobile "Browse" or a
  // spoofed extension can still bypass it).
  fileInput.accept =
    'image/png,image/jpeg,image/gif,image/webp,video/mp4,video/webm,application/pdf';

  const sendButton = document.createElement('button');
  sendButton.type = 'button';
  sendButton.className = `${ROOT_CLASS}-send`;
  sendButton.setAttribute('aria-label', t('sendMessage'));
  sendButton.append(sendIcon());

  row.append(textarea, emojiButton, attachButton, fileInput, sendButton);

  const footerNote = document.createElement('div');
  footerNote.className = `${ROOT_CLASS}-footer-note`;
  const footerNoteBrand = document.createElement('strong');
  footerNoteBrand.textContent = 'Flix';
  footerNote.append(t('poweredByPrefix'), footerNoteBrand);

  element.append(replyPreviewSlot, attachmentsContainer, errorText, row, footerNote);

  let ready = false;
  let sending = false;
  let nextLocalId = 0;
  const pendingAttachments = new Map<string, PendingAttachment>();

  function hasUploadingAttachment(): boolean {
    for (const attachment of pendingAttachments.values()) {
      if (attachment.status === 'uploading') {
        return true;
      }
    }

    return false;
  }

  function updateControlsDisabled(): void {
    const busy = sending || !ready;
    textarea.disabled = busy;
    sendButton.disabled = busy || hasUploadingAttachment();
    attachButton.disabled = sending || !ready;
  }

  function attemptSend(): void {
    if (sending || !ready || hasUploadingAttachment()) {
      return;
    }

    const text = textarea.value.trim();
    const attachmentIds: string[] = [];

    for (const attachment of pendingAttachments.values()) {
      if (attachment.status === 'uploaded' && attachment.attachmentId) {
        attachmentIds.push(attachment.attachmentId);
      }
    }

    if (text.length === 0 && attachmentIds.length === 0) {
      return;
    }

    handlers.onSend(text, attachmentIds);
  }

  // Grows the textarea with its content up to the existing CSS max-height
  // (92px) — resetting to 'auto' first is required so scrollHeight reports
  // the content's natural height rather than the current (possibly larger)
  // inline height. Past max-height, the browser's native textarea overflow
  // scroll takes over untouched; nothing here changes that.
  function autoResizeTextarea(): void {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      attemptSend();
    }
  }

  // Throttled, not debounced: the first keystroke of a burst fires
  // immediately (so Discord's typing indicator appears right away, not
  // after a delay), then further input is ignored until the cooldown
  // elapses — comfortably inside Discord's own ~10s indicator lifetime, so
  // it stays continuously refreshed while the customer keeps typing.
  let typingThrottleTimer: ReturnType<typeof setTimeout> | null = null;

  function handleTypingInput(): void {
    if (typingThrottleTimer !== null) {
      return;
    }

    handlers.onTyping();
    typingThrottleTimer = setTimeout(() => {
      typingThrottleTimer = null;
    }, TYPING_THROTTLE_MS);
  }

  function handleSendClick(): void {
    attemptSend();
  }

  function handleAttachClick(): void {
    fileInput.click();
  }

  function addPendingAttachment(localId: string, file: File): void {
    const cardElement = createAttachmentCardElement(file.name, file.size);
    cardElement.classList.add(`${ATTACHMENT_CLASS}--uploading`);

    const statusElement = document.createElement('span');
    statusElement.className = `${ATTACHMENT_CLASS}-status`;
    statusElement.textContent = '0%';

    // A thin progress bar under the filename, filled live from the upload's
    // byte progress (see setAttachmentProgress). Appended into the card's
    // meta column so it sits beneath the name/size, exactly like the
    // design mockup's "Upload in progress" state.
    const progressTrack = document.createElement('div');
    progressTrack.className = `${ATTACHMENT_CLASS}-progress`;
    const progressFill = document.createElement('div');
    progressFill.className = `${ATTACHMENT_CLASS}-progress-fill`;
    progressTrack.append(progressFill);
    cardElement.querySelector(`.${ATTACHMENT_CLASS}-meta`)?.append(progressTrack);

    const retryButton = document.createElement('button');
    retryButton.type = 'button';
    retryButton.className = `${ATTACHMENT_CLASS}-retry`;
    retryButton.setAttribute('aria-label', tf('retryUpload', { name: file.name }));
    retryButton.textContent = '↻';
    retryButton.hidden = true;
    retryButton.addEventListener('click', () => {
      const attachment = pendingAttachments.get(localId);
      if (attachment) {
        handlers.onRetryAttachment(localId, attachment.file);
      }
    });

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = `${ATTACHMENT_CLASS}-remove`;
    removeButton.setAttribute('aria-label', tf('removeAttachment', { name: file.name }));
    removeButton.textContent = '×';
    removeButton.addEventListener('click', () => {
      pendingAttachments.delete(localId);
      cardElement.remove();
      attachmentsContainer.hidden = pendingAttachments.size === 0;
      updateControlsDisabled();
    });

    cardElement.append(statusElement, retryButton, removeButton);
    attachmentsContainer.append(cardElement);
    attachmentsContainer.hidden = false;

    pendingAttachments.set(localId, {
      file,
      status: 'uploading',
      attachmentId: null,
      cardElement,
      statusElement,
      progressTrack,
      progressFill,
      retryButton,
    });
  }

  // Shared by the file picker and drag-and-drop: stage each file as a
  // pending card and hand the batch to the widget to upload.
  function ingestFiles(files: File[]): void {
    if (files.length === 0) {
      return;
    }

    const selected: PendingFile[] = [];

    for (const file of files) {
      const localId = `attachment-${nextLocalId++}`;
      addPendingAttachment(localId, file);
      selected.push({ localId, file });
    }

    updateControlsDisabled();
    handlers.onFilesSelected(selected);
  }

  function handleFileInputChange(): void {
    const files = fileInput.files;

    if (!files || files.length === 0) {
      return;
    }

    ingestFiles(Array.from(files));
    fileInput.value = '';
  }

  textarea.addEventListener('keydown', handleKeyDown);
  textarea.addEventListener('input', autoResizeTextarea);
  textarea.addEventListener('input', handleTypingInput);
  sendButton.addEventListener('click', handleSendClick);
  attachButton.addEventListener('click', handleAttachClick);
  fileInput.addEventListener('change', handleFileInputChange);

  // Without this, every control defaults to HTML's normal enabled state
  // until the first setReady/setSending call — a real (pre-existing, not
  // introduced by this redesign) bug caught via the Playwright pass in the
  // design integration notes: the send button visually looked clickable for
  // an instant right after creation, before the widget ever calls
  // setReady(true). attemptSend()'s own ready/sending guard meant a stray
  // click couldn't actually send anything, but the control's disabled
  // attribute should reflect reality from the moment it exists.
  updateControlsDisabled();

  return {
    element,

    setReplyPreview(preview: ReplyPreview | null): void {
      if (!preview) {
        replyPreviewSlot.hidden = true;
        return;
      }
      replyPreviewLabel.textContent = preview.label;
      replyPreviewSnippet.textContent = preview.snippet;
      replyPreviewSlot.hidden = false;
    },

    setReady(isReady: boolean): void {
      ready = isReady;
      updateControlsDisabled();
    },

    setSending(isSending: boolean): void {
      sending = isSending;
      updateControlsDisabled();
    },

    setSendError(message: string | null): void {
      errorText.textContent = message ?? '';
      errorText.hidden = message === null;
    },

    setAttachmentUploading(localId: string): void {
      const attachment = pendingAttachments.get(localId);

      if (!attachment) {
        return;
      }

      attachment.status = 'uploading';
      attachment.cardElement.classList.remove(`${ATTACHMENT_CLASS}--error`);
      attachment.cardElement.classList.add(`${ATTACHMENT_CLASS}--uploading`);
      attachment.statusElement.textContent = '0%';
      attachment.progressTrack.hidden = false;
      attachment.progressFill.style.width = '0%';
      attachment.retryButton.hidden = true;
      updateControlsDisabled();
    },

    setAttachmentProgress(localId: string, fraction: number): void {
      const attachment = pendingAttachments.get(localId);

      if (!attachment || attachment.status !== 'uploading') {
        return;
      }

      const percent = Math.max(0, Math.min(100, Math.round(fraction * 100)));
      attachment.progressFill.style.width = `${percent}%`;
      attachment.statusElement.textContent = `${percent}%`;
    },

    setAttachmentUploaded(localId: string, uploaded: UploadedAttachment): void {
      const attachment = pendingAttachments.get(localId);

      if (!attachment) {
        return;
      }

      attachment.status = 'uploaded';
      attachment.attachmentId = uploaded.id;
      attachment.cardElement.classList.remove(
        `${ATTACHMENT_CLASS}--uploading`,
        `${ATTACHMENT_CLASS}--error`,
      );
      attachment.statusElement.textContent = '';
      attachment.progressTrack.hidden = true;
      attachment.retryButton.hidden = true;
      updateControlsDisabled();
    },

    setAttachmentError(localId: string, message: string): void {
      const attachment = pendingAttachments.get(localId);

      if (!attachment) {
        return;
      }

      attachment.status = 'error';
      attachment.cardElement.classList.remove(`${ATTACHMENT_CLASS}--uploading`);
      attachment.cardElement.classList.add(`${ATTACHMENT_CLASS}--error`);
      attachment.statusElement.textContent = message;
      attachment.progressTrack.hidden = true;
      attachment.retryButton.hidden = false;
      updateControlsDisabled();
    },

    addFiles(files: File[]): void {
      ingestFiles(files);
    },

    clear(): void {
      textarea.value = '';
      autoResizeTextarea();
    },

    clearAttachments(): void {
      pendingAttachments.clear();
      attachmentsContainer.replaceChildren();
      attachmentsContainer.hidden = true;
      updateControlsDisabled();
    },

    focus(): void {
      textarea.focus();
    },

    destroy(): void {
      textarea.removeEventListener('keydown', handleKeyDown);
      textarea.removeEventListener('input', autoResizeTextarea);
      textarea.removeEventListener('input', handleTypingInput);
      sendButton.removeEventListener('click', handleSendClick);
      attachButton.removeEventListener('click', handleAttachClick);
      fileInput.removeEventListener('change', handleFileInputChange);

      if (typingThrottleTimer !== null) {
        clearTimeout(typingThrottleTimer);
        typingThrottleTimer = null;
      }
    },
  };
}
